import { useMemo } from "react";
import {
  Box,
  Typography,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Avatar,
  Card,
  Chip,
  Badge,
  Paper,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { lighten, darken } from "@mui/system";
import {
  AccountTree as AccountTreeIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import { MyFormData } from "./form-context";
import { useTranslation } from "react-i18next";
import useAuthenticatedQuery from "@/hooks/useAuthenticatedQuery";

export default function HazidNew() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { ptwData, setPtwData, allHazardsFromApi } = MyFormData();

  // Fetch workflows to group hazards
  const { data: workflowsData, isLoading: workflowsLoading } =
    useAuthenticatedQuery(["workflows-hazid"], "/api/v1/metadata/workflows");

  // Get all hazards from context (fresh from API) - memoized to prevent unnecessary re-renders
  const allHazards = useMemo(
    () => allHazardsFromApi || [],
    [allHazardsFromApi]
  );
  const selectedHazardIds = useMemo(
    () => ptwData.hazards.map((h) => h.id),
    [ptwData.hazards]
  );

  // Group hazards by workflow
  const groupedHazards = useMemo(() => {
    if (!allHazards || !workflowsData) return {};

    // Filter workflows that have target "WORK_PERMIT"
    const workPermitWorkflows = workflowsData.filter(
      (workflow) => workflow.target === "WORK_PERMIT"
    );

    return allHazards.reduce((acc, hazard) => {
      // Find workflows that contain this hazard
      const associatedWorkflows = workPermitWorkflows.filter((workflow) =>
        workflow.hazards?.some(
          (workflowHazard) => workflowHazard.id === hazard.id
        )
      );

      if (associatedWorkflows.length > 0) {
        // Group by the first associated workflow
        const workflowId = associatedWorkflows[0].id;

        if (!acc[workflowId]) {
          acc[workflowId] = {
            workflow: associatedWorkflows[0],
            hazards: [],
          };
        }
        acc[workflowId].hazards.push(hazard);
      } else {
        // Hazards not associated with any workflow go to "Unassigned"
        if (!acc["unassigned"]) {
          acc["unassigned"] = {
            workflow: { id: "unassigned", name: "Unassigned Hazards" },
            hazards: [],
          };
        }
        acc["unassigned"].hazards.push(hazard);
      }

      return acc;
    }, {});
  }, [allHazards, workflowsData]);

  if (!allHazards || allHazards.length === 0 || workflowsLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "300px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Handle adding a hazard from the available list
  const handleAddHazard = (hazardFromApi) => {
    setPtwData((prev) => {
      const hazardExists = prev.hazards.some((h) => h.id === hazardFromApi.id);
      if (hazardExists) return prev;

      // Get all precaution IDs (pre-select all precautions)
      const allPrecautionIds = (
        hazardFromApi.precaution_categories || []
      ).flatMap((category) => category.precautions?.map((p) => p.id) || []);

      // Create hazard object with API data including precaution_categories
      const newHazard = {
        id: hazardFromApi.id,
        name: hazardFromApi.name,
        likelihood: hazardFromApi.likelihood,
        severity_people: hazardFromApi.severity_people,
        severity_asset: hazardFromApi.severity_asset,
        severity_environment: hazardFromApi.severity_environment,
        severity_reputation: hazardFromApi.severity_reputation,
        precaution_categories: hazardFromApi.precaution_categories || [],
        precaution_ids: allPrecautionIds, // Pre-select all precautions
        residual_risk: {
          hazard_id: hazardFromApi.id,
          likelihood: hazardFromApi.likelihood,
          severity_people: hazardFromApi.severity_people,
          severity_asset: hazardFromApi.severity_asset,
          severity_environment: hazardFromApi.severity_environment,
          severity_reputation: hazardFromApi.severity_reputation,
        },
      };

      const updatedHazards = [...prev.hazards, newHazard];

      // Sync with localStorage
      localStorage.setItem("workflowHazards", JSON.stringify(updatedHazards));

      return {
        ...prev,
        hazards: updatedHazards,
      };
    });
  };

  // Handle removing a hazard from selection
  const handleRemoveHazard = (hazardId) => {
    setPtwData((prev) => {
      const updatedHazards = prev.hazards.filter((h) => h.id !== hazardId);

      // Sync with localStorage
      localStorage.setItem("workflowHazards", JSON.stringify(updatedHazards));

      return {
        ...prev,
        hazards: updatedHazards,
      };
    });
  };

  const isDarkMode = theme.palette.mode === "dark";

  return (
    <Box sx={{ display: "flex", gap: 3 }}>
      {/* Left side: Available hazards grouped by workflow */}
      <Paper
        elevation={3}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          borderRadius: 3,
          overflow: "hidden",
          backgroundColor: isDarkMode
            ? darken(theme.palette.background.paper, 0.02)
            : lighten(theme.palette.background.paper, 0.5),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 3,
            py: 2,
            pt: 3,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        >
          <CategoryIcon color="primary" />
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 600,
              flex: 1,
            }}
          >
            {t("hazardPicker.availableHazards")}
          </Typography>
          <Chip
            label={`${allHazards.length} ${t("hazards.total")}`}
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              color: "primary.main",
              fontWeight: 600,
            }}
          />
        </Box>

        {/* Content */}
        <Box
          sx={{
            overflowY: "auto",
            p: 3,
            maxHeight: "550px",
          }}
        >
          {Object.keys(groupedHazards).length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {Object.entries(groupedHazards).map(([workflowId, group]) => (
                <Card
                  key={workflowId}
                  elevation={0}
                  sx={{
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    borderRadius: 2,
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                    },
                  }}
                >
                  {/* Workflow Group Header */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 2,
                      background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: "primary.main",
                        width: 36,
                        height: 36,
                        boxShadow: 1,
                      }}
                    >
                      <AccountTreeIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="700"
                        color="text.primary"
                        sx={{ mb: 0.25 }}
                      >
                        {group.workflow.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <WarningIcon sx={{ fontSize: 14 }} />
                        {group.hazards.length} {t("hazards.hazardsInGroup")}
                      </Typography>
                    </Box>
                    <Badge
                      badgeContent={
                        group.hazards.filter((h) =>
                          selectedHazardIds.includes(h.id)
                        ).length
                      }
                      color="success"
                      max={999}
                    >
                      <Chip
                        label={t("hazards.selected")}
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    </Badge>
                  </Box>

                  {/* Hazards in this group */}
                  <List sx={{ p: 1, pt: 0.5 }}>
                    {group.hazards.map((hazard, index) => {
                      const isSelected = selectedHazardIds.includes(hazard.id);
                      return (
                        <ListItem
                          key={hazard.id}
                          sx={{
                            bgcolor: isSelected
                              ? alpha(theme.palette.success.main, 0.08)
                              : "transparent",
                            borderRadius: 1.5,
                            mb: index < group.hazards.length - 1 ? 0.5 : 0,
                            py: 0.75,
                            border: `1px solid ${isSelected ? alpha(theme.palette.success.main, 0.3) : "transparent"}`,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: isSelected
                                ? alpha(theme.palette.success.main, 0.12)
                                : alpha(theme.palette.primary.main, 0.04),
                              transform: "translateX(4px)",
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Checkbox
                              edge="start"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  handleAddHazard(hazard);
                                } else {
                                  handleRemoveHazard(hazard.id);
                                }
                              }}
                              sx={{
                                color: alpha(theme.palette.primary.main, 0.5),
                                "&.Mui-checked": {
                                  color: "success.main",
                                },
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={hazard.name}
                            slotProps={{
                              primary: {
                                variant: "body2",
                                fontWeight: isSelected ? 600 : 500,
                                color: isSelected
                                  ? "success.dark"
                                  : "text.primary",
                              },
                            }}
                          />
                          {isSelected && (
                            <CheckCircleIcon
                              sx={{
                                color: "success.main",
                                fontSize: 20,
                                ml: 1,
                              }}
                            />
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                </Card>
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
                gap: 2,
              }}
            >
              <WarningIcon
                sx={{
                  fontSize: 64,
                  color: alpha(theme.palette.text.secondary, 0.3),
                }}
              />
              <Typography
                variant="body1"
                color="text.secondary"
                fontWeight={500}
              >
                {t("hazardPicker.noHazardsAvailable")}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            bgcolor: alpha(theme.palette.background.paper, 0.5),
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {t("hazardPicker.selectHazardsToAdd")}
          </Typography>
        </Box>
      </Paper>

      {/* Right side: Selected hazards list */}
      <Paper
        elevation={3}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          borderRadius: 3,
          overflow: "hidden",
          backgroundColor: isDarkMode
            ? darken(theme.palette.background.paper, 0.02)
            : lighten(theme.palette.background.paper, 0.5),
          border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 3,
            py: 2,
            pt: 3,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        >
          <CheckCircleIcon color="success" />
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.success.main,
              fontWeight: 600,
              flex: 1,
            }}
          >
            {t("hazardPicker.selectedHazards")}
          </Typography>
          <Chip
            label={`${selectedHazardIds.length} ${t("hazards.selected")}`}
            size="small"
            color="success"
            sx={{
              fontWeight: 600,
              px: 1,
            }}
          />
        </Box>

        {/* Content */}
        <Box
          sx={{
            overflowY: "auto",
            p: 3,
            maxHeight: "550px",
          }}
        >
          {selectedHazardIds.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                gap: 2,
                py: 8,
              }}
            >
              <CheckCircleIcon
                sx={{
                  fontSize: 80,
                  color: alpha(theme.palette.success.main, 0.2),
                }}
              />
              <Typography
                variant="h6"
                color="text.secondary"
                fontWeight={600}
                textAlign="center"
              >
                {t("hazardPicker.noHazardsSelected")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ maxWidth: 300 }}
              >
                {t("hazardPicker.selectHazardsFromLeft")}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {ptwData.hazards.map((hazard, index) => (
                <Card
                  key={hazard.id}
                  elevation={0}
                  sx={{
                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                    borderRadius: 2,
                    overflow: "hidden",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.2)}`,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <ListItem
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.08),
                      p: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        gap: 1.5,
                      }}
                    >
                      <Chip
                        label={index + 1}
                        size="small"
                        sx={{
                          bgcolor: "success.main",
                          color: "success.contrastText",
                          fontWeight: 700,
                          minWidth: 32,
                          height: 24,
                        }}
                      />
                      <ListItemText
                        primary={hazard.name}
                        slotProps={{
                          primary: {
                            variant: "body2",
                            fontWeight: 600,
                            color: "success.dark",
                          },
                        }}
                        sx={{ flex: 1 }}
                      />
                      <Checkbox
                        edge="end"
                        checked={true}
                        onChange={() => handleRemoveHazard(hazard.id)}
                        sx={{
                          color: "success.main",
                          "&.Mui-checked": {
                            color: "success.main",
                          },
                        }}
                      />
                    </Box>
                  </ListItem>
                </Card>
              ))}
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            bgcolor: alpha(theme.palette.background.paper, 0.5),
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {t("hazardPicker.reviewSelectedHazards")}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
